import React from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Moon, Sun, LogOut, Database, LayoutDashboard, Upload, Users, ClipboardCheck, Download, Home, CalendarDays, FileArchive, FileDown, User, Sparkles } from "lucide-react";
import NotificationBell from "@/components/NotificationBell";

const adminNav = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, testid: "nav-admin-overview" },
  { to: "/admin/uploads", label: "Uploads", icon: Upload, testid: "nav-admin-uploads" },
  { to: "/admin/approvals", label: "Approvals", icon: ClipboardCheck, testid: "nav-admin-approvals" },
  { to: "/admin/customers", label: "Customers", icon: Users, testid: "nav-admin-customers" },
  { to: "/admin/data", label: "Data Export", icon: Download, testid: "nav-admin-data" },
  { to: "/admin/samples", label: "Samples", icon: Sparkles, testid: "nav-admin-samples" },
];

const customerNav = [
  { to: "/dashboard/daily", label: "Daily Updates", icon: CalendarDays, testid: "nav-customer-daily" },
  { to: "/dashboard/database", label: "Database", icon: FileArchive, testid: "nav-customer-database" },
  { to: "/dashboard/export", label: "Data Export", icon: FileDown, testid: "nav-customer-export" },
  { to: "/dashboard/package", label: "My Package", icon: User, testid: "nav-customer-package" },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const isAdmin = user?.role === "admin";
  const items = isAdmin ? adminNav : customerNav;

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-50 border-b border-border glass-header" data-testid="app-header">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <Link to={isAdmin ? "/admin" : "/dashboard/daily"} className="flex items-center gap-2" data-testid="brand-link">
            <div className="h-8 w-8 rounded-md bg-primary text-primary-foreground grid place-items-center font-heading font-bold text-lg">R</div>
            <div className="leading-tight">
              <div className="font-heading font-bold text-lg">Registry<span className="text-primary">Feed</span></div>
              <div className="text-[10px] tracking-widest text-muted-foreground uppercase">GST · MCA Data</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/admin" || it.to === "/dashboard/daily"}
                data-testid={it.testid}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:text-foreground hover:bg-accent"
                  }`
                }
              >
                <it.icon className="h-4 w-4" strokeWidth={1.75} />
                {it.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <NotificationBell />
            <Button
              variant="ghost"
              size="icon"
              onClick={toggle}
              data-testid="theme-toggle"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <div className="hidden sm:flex items-center gap-2 px-2 py-1 rounded-md border border-border">
              <div className="h-7 w-7 rounded bg-muted grid place-items-center text-xs font-semibold" data-testid="user-avatar">
                {user?.name?.[0]?.toUpperCase() || "U"}
              </div>
              <div className="text-xs leading-tight">
                <div className="font-medium">{user?.name}</div>
                <div className="text-muted-foreground">{isAdmin ? "Admin" : "Customer"}</div>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="h-4 w-4 mr-1.5" /> Logout
            </Button>
          </div>
        </div>
        {/* Mobile nav */}
        <div className="md:hidden border-t border-border">
          <div className="mx-auto max-w-[1400px] px-4 py-2 flex gap-1 overflow-x-auto">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/admin" || it.to === "/dashboard/daily"}
                data-testid={`${it.testid}-mobile`}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm whitespace-nowrap ${
                    isActive ? "bg-primary/10 text-primary" : "text-muted-foreground"
                  }`
                }
              >
                <it.icon className="h-4 w-4" strokeWidth={1.75} />
                {it.label}
              </NavLink>
            ))}
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-[1400px] px-4 sm:px-6 py-6 sm:py-10">
        <Outlet />
      </main>
    </div>
  );
}
